/**
 * Shared tour-gating hooks.
 *
 * `useTourGate` builds the live permission/module gate the tour engine, the
 * sidebar and the launchers all use. `useRouteTourId` resolves the tour for the
 * page in focus -- the header TourLauncher's rule, shared with the help
 * assistant's "Take a tour" (#3604) so both offer exactly the same tour.
 */
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/auth";
import { useModules } from "../../contexts/modules";
import { getTour } from "./registry";
import { isTourAvailable } from "./filter";
import { tourIdForRoute } from "./routeTours";
import { routeMatchesStep } from "./routeMatch";
import type { TourGateContext } from "./types";

export function useTourGate(): TourGateContext {
  const { user } = useAuth();
  const { isEnabled } = useModules();
  return useMemo(
    () => ({
      has: (flag) => Boolean(user?.permission_flags?.[flag]),
      isModuleEnabled: (module) => isEnabled(module),
      isAdmin: user?.role === "admin",
      preset: user?.permission_preset ?? null,
    }),
    [user, isEnabled],
  );
}

/**
 * The tour registered for the current route, or null when there is none or the
 * engine would not offer it to this user.
 *
 * #2688: the resolved tour's FIRST step's route must also match the concrete
 * path. Prefix routes match on a path-segment prefix, so e.g. the bare `/legal`
 * list would otherwise resolve to the File Cover Page tour whose steps live on
 * `/legal/:otpId`. `routeMatchesStep` is the engine's own `:param`-aware matcher.
 */
export function useRouteTourId(): string | null {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { loading: modulesLoading } = useModules();
  const gate = useTourGate();

  return useMemo(() => {
    if (authLoading || modulesLoading) return null;
    const id = tourIdForRoute(location.pathname, user?.role, user?.permission_preset);
    if (!id) return null;
    const tour = getTour(id);
    if (!tour || !isTourAvailable(tour, gate)) return null;
    const openingRoute = tour.steps[0]?.route;
    if (!openingRoute || !routeMatchesStep(location.pathname, openingRoute)) {
      return null;
    }
    return id;
  }, [authLoading, modulesLoading, location.pathname, user?.role, user?.permission_preset, gate]);
}
